import { AppObject, OcctImportMesh } from '../types'

const IDENTITY_MATRIX = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]

/** Apply a column-major 4x4 matrix to a point. */
function transformPoint(m: number[], x: number, y: number, z: number): [number, number, number] {
  const w = m[3] * x + m[7] * y + m[11] * z + m[15] || 1
  return [
    (m[0] * x + m[4] * y + m[8] * z + m[12]) / w,
    (m[1] * x + m[5] * y + m[9] * z + m[13]) / w,
    (m[2] * x + m[6] * y + m[10] * z + m[14]) / w,
  ]
}

/** Apply the rotation (upper 3x3) of a matrix to a normal and re-normalize. */
function transformNormal(m: number[], x: number, y: number, z: number): [number, number, number] {
  const nx = m[0] * x + m[4] * y + m[8] * z
  const ny = m[1] * x + m[5] * y + m[9] * z
  const nz = m[2] * x + m[6] * y + m[10] * z
  const len = Math.hypot(nx, ny, nz) || 1
  return [nx / len, ny / len, nz / len]
}

/**
 * Merge several parts into one. The preview geometry bakes each part's move offset;
 * the export geometry bakes each part's node transform + offset into a common frame
 * (with an identity node transform) so the faithful GLB round-trip still places it
 * correctly. Material and flags come from the first part.
 */
export function joinObjects(objects: AppObject[]): AppObject | null {
  if (objects.length < 2) return null
  const primary = objects[0]
  const hasUv = objects.every((o) => !!o.mesh.attributes.uv)

  // Preview geometry — bake each part's offset into positions.
  const wPos: number[] = []
  const wNorm: number[] = []
  const wUv: number[] = []
  const wIdx: number[] = []
  let wBase = 0
  for (const o of objects) {
    const p = o.mesh.attributes.position.array
    const nm = o.mesh.attributes.normal.array
    const uv = o.mesh.attributes.uv?.array
    const ix = o.mesh.index?.array
    const off = o.offset ?? [0, 0, 0]
    const vc = p.length / 3
    for (let i = 0; i < vc; i++) {
      wPos.push(p[i * 3] + off[0], p[i * 3 + 1] + off[1], p[i * 3 + 2] + off[2])
      wNorm.push(nm[i * 3], nm[i * 3 + 1], nm[i * 3 + 2])
      if (hasUv && uv) wUv.push(uv[i * 2], uv[i * 2 + 1])
    }
    if (ix) for (let k = 0; k < ix.length; k++) wIdx.push(ix[k] + wBase)
    else for (let k = 0; k < vc; k++) wIdx.push(k + wBase)
    wBase += vc
  }

  // Export geometry — bake node transform + offset to world; identity node transform.
  const allSrc = objects.every((o) => !!o.sourceAttributes)
  let sourceAttributes: AppObject['sourceAttributes']
  let sourceTransform: number[] | undefined
  if (allSrc) {
    const srcHasUv = objects.every((o) => !!o.sourceAttributes!.uv)
    const srcHasNorm = objects.every((o) => !!o.sourceAttributes!.normal)
    const sPos: number[] = []
    const sNorm: number[] = []
    const sUv: number[] = []
    const sIdx: number[] = []
    let sBase = 0
    for (const o of objects) {
      const s = o.sourceAttributes!
      const m = o.sourceTransform
      const off = o.offset ?? [0, 0, 0]
      const vc = s.position.length / 3
      for (let i = 0; i < vc; i++) {
        const [x, y, z] = m
          ? transformPoint(m, s.position[i * 3], s.position[i * 3 + 1], s.position[i * 3 + 2])
          : [s.position[i * 3], s.position[i * 3 + 1], s.position[i * 3 + 2]]
        sPos.push(x + off[0], y + off[1], z + off[2])
        if (srcHasNorm && s.normal) {
          const [nx, ny, nz] = m
            ? transformNormal(m, s.normal[i * 3], s.normal[i * 3 + 1], s.normal[i * 3 + 2])
            : [s.normal[i * 3], s.normal[i * 3 + 1], s.normal[i * 3 + 2]]
          sNorm.push(nx, ny, nz)
        }
        if (srcHasUv && s.uv) sUv.push(s.uv[i * 2], s.uv[i * 2 + 1])
      }
      const ix = s.index
      if (ix) for (let k = 0; k < ix.length; k++) sIdx.push(ix[k] + sBase)
      else for (let k = 0; k < vc; k++) sIdx.push(k + sBase)
      sBase += vc
    }
    sourceAttributes = {
      position: new Float32Array(sPos),
      normal: srcHasNorm ? new Float32Array(sNorm) : undefined,
      uv: srcHasUv ? new Float32Array(sUv) : undefined,
      index: new Uint32Array(sIdx),
    }
    // Identity (not undefined) so the faithful-export round-trip still triggers.
    sourceTransform = [...IDENTITY_MATRIX]
  }

  const mesh: OcctImportMesh = {
    id: primary.id,
    color: primary.mesh.color,
    attributes: {
      position: { array: new Float32Array(wPos) },
      normal: { array: new Float32Array(wNorm) },
      ...(hasUv ? { uv: { array: new Float32Array(wUv) } } : {}),
    },
    index: { array: new Uint32Array(wIdx) },
  }

  return {
    id: primary.id,
    color: primary.color,
    mesh,
    material: primary.material ? { ...primary.material } : undefined,
    offset: undefined,
    hidden: primary.hidden,
    logoV: primary.logoV,
    sourceTransform,
    sourceAttributes,
  }
}

/**
 * Split a part into its disconnected pieces (connected components).
 *
 * Triangles are grouped by connectivity, welding vertices that share a position
 * first — otherwise the hard-edge duplicate vertices GLBs use would split a single
 * visual piece at every crease. Each resulting piece becomes its own AppObject with
 * its share of the geometry (and the matching source geometry so the export stays
 * faithful). Returns null when the part is already a single connected piece.
 */
export function splitObjectByConnectivity(object: AppObject): AppObject[] | null {
  const pos = object.mesh.attributes.position?.array
  const norm = object.mesh.attributes.normal?.array
  const uv = object.mesh.attributes.uv?.array
  const idx = object.mesh.index?.array
  if (!pos || !norm || !idx) return null

  const vertCount = pos.length / 3

  // Weld by position: each vertex points at the first vertex sharing its position.
  const posKey = new Map<string, number>()
  const rep = new Int32Array(vertCount)
  const q = (v: number) => Math.round(v * 1e4) // ~1e-4 model-unit tolerance
  for (let i = 0; i < vertCount; i++) {
    const key = `${q(pos[i * 3])},${q(pos[i * 3 + 1])},${q(pos[i * 3 + 2])}`
    const found = posKey.get(key)
    if (found === undefined) {
      posKey.set(key, i)
      rep[i] = i
    } else {
      rep[i] = found
    }
  }

  // Union-Find over the welded representatives, joined along triangle edges.
  const parent = new Int32Array(vertCount)
  for (let i = 0; i < vertCount; i++) parent[i] = i
  const find = (x: number): number => {
    let r = rep[x]
    while (parent[r] !== r) {
      parent[r] = parent[parent[r]]
      r = parent[r]
    }
    return r
  }
  const union = (a: number, b: number) => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent[ra] = rb
  }
  for (let t = 0; t < idx.length; t += 3) {
    union(idx[t], idx[t + 1])
    union(idx[t + 1], idx[t + 2])
  }

  // Group triangles by their component root.
  const groups = new Map<number, number[]>()
  for (let t = 0; t < idx.length; t += 3) {
    const root = find(idx[t])
    let arr = groups.get(root)
    if (!arr) {
      arr = []
      groups.set(root, arr)
    }
    arr.push(t)
  }
  if (groups.size <= 1) return null

  const src = object.sourceAttributes
  const results: AppObject[] = []
  let n = 0

  for (const tris of groups.values()) {
    const remap = new Map<number, number>()
    const nPos: number[] = []
    const nNorm: number[] = []
    const nUv: number[] = []
    const nIdx: number[] = []
    const sPos: number[] = []
    const sNorm: number[] = []
    const sUv: number[] = []

    for (const t of tris) {
      for (let k = 0; k < 3; k++) {
        const vi = idx[t + k]
        let ni = remap.get(vi)
        if (ni === undefined) {
          ni = remap.size
          remap.set(vi, ni)
          nPos.push(pos[vi * 3], pos[vi * 3 + 1], pos[vi * 3 + 2])
          nNorm.push(norm[vi * 3], norm[vi * 3 + 1], norm[vi * 3 + 2])
          if (uv) nUv.push(uv[vi * 2], uv[vi * 2 + 1])
          if (src) {
            sPos.push(src.position[vi * 3], src.position[vi * 3 + 1], src.position[vi * 3 + 2])
            if (src.normal) sNorm.push(src.normal[vi * 3], src.normal[vi * 3 + 1], src.normal[vi * 3 + 2])
            if (src.uv) sUv.push(src.uv[vi * 2], src.uv[vi * 2 + 1])
          }
        }
        nIdx.push(ni)
      }
    }

    const partId = `${object.id}_${++n}`
    const newMesh: OcctImportMesh = {
      id: partId,
      color: object.mesh.color,
      attributes: {
        position: { array: new Float32Array(nPos) },
        normal: { array: new Float32Array(nNorm) },
        ...(uv ? { uv: { array: new Float32Array(nUv) } } : {}),
      },
      index: { array: new Uint32Array(nIdx) },
    }

    results.push({
      id: partId,
      color: object.color,
      mesh: newMesh,
      material: object.material ? { ...object.material } : undefined,
      offset: object.offset ? [...object.offset] : undefined,
      hidden: object.hidden,
      logoV: object.logoV,
      sourceTransform: object.sourceTransform ? [...object.sourceTransform] : undefined,
      // Shared by reference: the halves are still the same authored surface, and its maps
      // are addressed by UVs that both halves inherit. (Tangents are not carried across a
      // split — three derives a frame per pixel when they are absent.)
      sourceMaterial: object.sourceMaterial,
      sourceAttributes: src
        ? {
            position: new Float32Array(sPos),
            normal: src.normal ? new Float32Array(sNorm) : undefined,
            uv: src.uv ? new Float32Array(sUv) : undefined,
            index: new Uint32Array(nIdx),
          }
        : undefined,
    })
  }

  return results
}
